import { useMemo, type ReactNode } from "react";
import { createPath, Router, type Navigator, type To } from "react-router";
import { DraftGuardContext, type DraftGuardContextValue } from "../../features/processAuthoring/draft/draftGuardContext";
import { modifiedLinkClick } from "./tabs/modifiedLinkClick";
import type { WorkspaceOwner } from "./useWorkspaceOwner";

const pathOf = (to: To): string => (typeof to === "string" ? to : createPath(to));

/**
 * The window's chrome (the top bar) reads and moves the active tab (F90): its location
 * is the active tab's, and its links and `navigate` go to that tab, through the tab's draft
 * guard, as a link inside it would, also with Ctrl, Alt or a middle click. Leaving the
 * account asks every tab's guard first.
 */
export default function ChromeRouter({ owner, children }: { owner: WorkspaceOwner; children: ReactNode }) {
  const { controller, snapshot } = owner;
  const navigator = useMemo<Navigator>(
    () => ({
      createHref: pathOf,
      push: (to, state: unknown) => { controller.navigateActive(pathOf(to), false, state); },
      replace: (to, state: unknown) => { controller.navigateActive(pathOf(to), true, state); },
      go: (delta) => { window.history.go(delta); },
    }),
    [controller],
  );
  const guard = useMemo<DraftGuardContextValue>(
    () => ({ register: () => () => undefined, requestClose: (close) => { controller.requestCloseAll(close); } }),
    [controller],
  );
  const openModified = useMemo(() => modifiedLinkClick((route, placement) => { owner.chrome.open(route, { placement }); }), [owner.chrome]);
  return (
    <Router location={snapshot.activeLocation} navigator={navigator}>
      <DraftGuardContext.Provider value={guard}>
        <div className="contents" onClickCapture={openModified} onAuxClickCapture={openModified}>{children}</div>
      </DraftGuardContext.Provider>
    </Router>
  );
}
